import { useState, useMemo } from 'react'

import api from '../../../utils/api'
import Input from '../../../shared/components/form/input'
import Button from '../../../shared/components/Button/Button'
import Checkbox from '../../../shared/components/form/checkbox'
import FormLayout from '../../../shared/components/FormLayout/FormLayout'
import buildCategoryOptions from '../../../shared/functions/buildCategoryOptions'
import useFlashMessage from '../../../hooks/useFlashMessage'
import AllocationsField, { allocationsMismatch } from '../AllocationsField'

function FormFinanceRecurring({ type, categories, editingTemplate, btCancelAction, handleOnSuccess }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories])

    function initialAllocations() {
        if (!editingTemplate) return [{ category: null, value: '' }]
        return editingTemplate.allocations.map((a) => ({
            category: categoryOptions.find((o) => o.value === (a.category?._id || a.category)) || null,
            value: String(a.value),
        }))
    }

    const [description, setDescription] = useState(editingTemplate?.description || '')
    const [counterpartName, setCounterpartName] = useState(editingTemplate?.counterpart?.name || '')
    const [totalValue, setTotalValue] = useState(editingTemplate ? String(editingTemplate.totalValue) : '')
    const [allocations, setAllocations] = useState(initialAllocations)
    const [dayOfMonth, setDayOfMonth] = useState(editingTemplate ? String(editingTemplate.dayOfMonth) : '5')
    const [active, setActive] = useState(editingTemplate ? editingTemplate.active : true)
    const [endDate, setEndDate] = useState(editingTemplate?.endDate ? editingTemplate.endDate.substring(0, 10) : '')

    async function submit(e) {
        e.preventDefault()

        if (!description) {
            setFlashMessage('Informe a descrição', 'error')
            return
        }
        const value = parseFloat(totalValue)
        if (!value || value <= 0) {
            setFlashMessage('Informe um valor total válido', 'error')
            return
        }
        if (allocations.some((a) => !a.category)) {
            setFlashMessage('Selecione a categoria de cada linha do rateio', 'error')
            return
        }
        if (allocationsMismatch(allocations, totalValue)) {
            setFlashMessage('A soma do rateio entre categorias precisa bater com o valor total', 'error')
            return
        }
        const day = parseInt(dayOfMonth)
        if (!day || day < 1 || day > 28) {
            setFlashMessage('O dia do vencimento precisa ser entre 1 e 28', 'error')
            return
        }

        const body = {
            type,
            description,
            counterpart: counterpartName ? { kind: type === 'pagar' ? 'provider' : 'client', name: counterpartName } : undefined,
            totalValue: value,
            allocations: allocations.map((a) => ({ category: a.category.value, value: parseFloat(a.value) || 0 })),
            dayOfMonth: day,
            active,
            endDate: endDate || null,
        }

        let msgType = 'success'
        const request = editingTemplate
            ? api.patch(`/finance/recurring/${editingTemplate._id}`, body, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            : api.post('/finance/recurring/new', body, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })

        const data = await request.then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') handleOnSuccess()
    }

    return (
        <FormLayout
            title={editingTemplate ? 'Editar Conta Fixa' : (type === 'pagar' ? 'Nova Conta Fixa a Pagar' : 'Nova Conta Fixa a Receber')}
            onSubmit={submit}
            actions={
                <>
                    <Button variant={editingTemplate ? 'save' : 'new'} type="submit">{editingTemplate ? 'Salvar Alterações' : 'Cadastrar'}</Button>
                    <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                </>
            }
        >
            <div className="fl-row">
                <div className="fl-col-wide form-group">
                    <Input text="Descrição" type="text" name="description" placeholder="Ex.: Aluguel" handleOnChange={(e) => setDescription(e.target.value)} value={description} />
                </div>
                <div className="fl-col-mid form-group">
                    <Input text={type === 'pagar' ? 'Fornecedor (opcional)' : 'Cliente (opcional)'} type="text" name="counterpartName" placeholder="Nome" handleOnChange={(e) => setCounterpartName(e.target.value)} value={counterpartName} />
                </div>
            </div>

            <div className="fl-row">
                <div className="form-group">
                    <Input text="Valor mensal (R$)" type="number" name="totalValue" placeholder="0.00" handleOnChange={(e) => setTotalValue(e.target.value)} value={totalValue} />
                </div>
                <div className="form-group">
                    <Input text="Dia do vencimento (1-28)" type="number" name="dayOfMonth" placeholder="5" handleOnChange={(e) => setDayOfMonth(e.target.value)} value={dayOfMonth} />
                </div>
                <div className="form-group">
                    <Input text="Encerra em (opcional)" type="date" name="endDate" handleOnChange={(e) => setEndDate(e.target.value)} value={endDate} />
                </div>
            </div>

            <AllocationsField categoryOptions={categoryOptions} totalValue={totalValue} allocations={allocations} setAllocations={setAllocations} />

            <div className="form-group">
                <Checkbox text="Ativa (gera lançamento todo mês)" check={active} handleOnChange={() => setActive(!active)} />
            </div>
        </FormLayout>
    )
}

export default FormFinanceRecurring
